// packages/web/src/lib/api/client.ts
// Type-safe API client with predefined endpoints and circuit breaker protection

import { apiFetchWithCircuitBreaker } from './circuit-breaker';
import { getApiEndpoint, getEndpoint } from './endpoints';

// Type definitions for API responses
export interface BalanceResponse {
  available_balance_cents: number;
  total_balance_cents: number;
  processing_balance_cents: number;
  arriving_balance_cents: number;
  pending_earned_cents: number;
  credit_balance_cents: number;
  currency: string;
  last_updated: string;
  states: {
    sale_made: StateData;
    processing: StateData;
    arriving: StateData;
    available: StateData;
  };
  service_visibility: {
    visible_services: number;
    hidden_services: number;
    balance_needed_for_all: number;
  };
  source: string;
}

export interface PartnerBalancesResponse {
  // 4-state wallet balances
  sale_made_cents: number; // Pending earnings from sales
  processing_cents: number; // In processing
  arriving_cents: number; // Funds arriving
  available_cents: number; // Available for withdrawal/use

  // Aggregated totals
  total_earned_cents: number;
  total_available_cents: number;
  total_pending_cents: number;

  // Platform fees (separate from wallet)
  platform_fees_balance_cents: number;

  // Transaction history (recent)
  recent_transactions: Array<{
    id: string;
    amount_cents: number;
    partner_state: string;
    transaction_type: string;
    created_at: string;
    notes?: string;
  }>;

  // Metadata
  last_updated: string;
  currency: string;
}

export interface PartnerPlatformCreditsResponse {
  platform_credits_cents: number;
  total_credited_cents: number;
  available_credits_cents: number;
  transaction_history: Array<any>; // Platform fee transactions
  last_updated: string;
  currency: string;
  description: string;
}

export interface StateData {
  count: number;
  total_cents: number;
  estimated_payment_range?: string;
  next_payment_estimate?: string | null;
  next_available?: string | null;
}

export interface TransactionResponse {
  transactions: WalletTransaction[];
  total_count: number;
  pagination: {
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

export interface WalletTransaction {
  id: string;
  transaction_type: string;
  amount_cents: number;
  currency: string;
  partner_state: string;
  created_at: string;
  available_at?: string;
  processed_at?: string;
  notes?: string;
}

export interface DepositRequest {
  amount: number;
  paymentMethodId?: string;
}

export interface DepositResponse {
  success: boolean;
  transactionId: string;
  newBalance: number;
}

export interface WithdrawalHistoryItem {
  id: string;
  amount_cents: number;
  payout_method: 'ach' | 'check';
  status: 'pending' | 'processing' | 'completed' | 'cancelled';
  notes?: string;
  requested_at: string;
  processed_at?: string;
}

export interface CommissionProjectionRequest {
  commissionConfig: any;
  serviceName: string;
  expectedMonthlySales: number;
  averageSaleAmount: number;
  projectionMonths: number;
}

export interface CommissionProjectionResponse {
  projections: Array<{
    month: string;
    estimatedEarnings: number;
    cumulativeEarnings: number;
  }>;
  totalProjected: number;
}

export interface OrderResponse {
  orders: any[];
  total: number;
  pagination: {
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

export interface BrandResponse {
  brands: any[];
}

export interface ServiceResponse {
  services: any[];
  total: number;
  pagination?: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
    currentPage: number;
    totalPages: number;
  };
  filters?: any;
}

// Type-safe API client class
export class ApiClient {
  // ====================
  // BANKING APIs
  // ====================

  static async getBalance(): Promise<BalanceResponse> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('banking', 'balance'));
  }

  static async getPartnerBalances(): Promise<PartnerBalancesResponse> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('banking', 'balances'));
  }

  static async getPartnerPlatformCredits(): Promise<PartnerPlatformCreditsResponse> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('banking', 'platformCredits'));
  }

  static async getTransactions(
    limit = 50,
    offset = 0,
    state?: string,
  ): Promise<TransactionResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });
    if (state) params.append('state', state);

    return apiFetchWithCircuitBreaker(`${getApiEndpoint('banking', 'transactions')}?${params}`);
  }

  static async createDeposit(request: DepositRequest): Promise<DepositResponse> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('banking', 'deposit'), {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  static async createWithdrawal(
    amount: number,
    currency = 'usd',
    description?: string,
  ): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('banking', 'withdraw'), {
      method: 'POST',
      body: JSON.stringify({ amount, currency, description }),
    });
  }

  static async requestWithdrawal(request: {
    amount_cents: number;
    payout_method: 'ach' | 'check';
    notes?: string;
  }): Promise<{ success: boolean; request_id: string; estimated_arrival: string }> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('banking', 'withdrawalRequest'), {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  static async getWithdrawalHistory(): Promise<{ success: boolean; data: { withdrawals: WithdrawalHistoryItem[] } }> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('banking', 'withdrawalHistory'));
  }

  static async getBankingStatus(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('banking', 'status'));
  }

  static async connectStripeAccount(businessInfo: any): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('banking', 'connect'), {
      method: 'POST',
      body: JSON.stringify(businessInfo),
    });
  }

  static async getOnboardingLink(returnUrl: string, refreshUrl: string): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('banking', 'onboard'), {
      method: 'POST',
      body: JSON.stringify({ return_url: returnUrl, refresh_url: refreshUrl }),
    });
  }

  static async getAutoReplenishSettings(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('banking', 'autoReplenish'));
  }

  static async updateAutoReplenishSettings(settings: any): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('banking', 'autoReplenish'), {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  static async processAutoRefill(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('banking', 'autoReplenish') + '/process', {
      method: 'POST',
    });
  }

  static async recalculateEscrow(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('banking', 'recalculateEscrow'), {
      method: 'POST',
    });
  }

  // ====================
  // PERKS APIs
  // ====================

  static async getPerks(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('perks', 'list'));
  }

  static async createPerk(perkData: any): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('perks', 'list'), {
      method: 'POST',
      body: JSON.stringify(perkData),
    });
  }

  static async updatePerk(perkId: string, perkData: any): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('perks', 'list')}/${perkId}`, {
      method: 'PUT',
      body: JSON.stringify(perkData),
    });
  }

  static async deletePerk(perkId: string): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('perks', 'list')}/${perkId}`, {
      method: 'DELETE',
    });
  }

  static async updatePerkStatus(perkId: string, status: { status: string }): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('perks', 'list')}/${perkId}/status`, {
      method: 'PATCH',
      body: JSON.stringify(status),
    });
  }

  static async getPerksDeposits(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('perks', 'deposits'));
  }

  static async addPerksDeposit(amount: number): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('perks', 'deposits'), {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
  }

  static async getPerksDepositTransactions(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('perks', 'depositTransactions'));
  }

  static async getPerksImpact(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('perks', 'impact'));
  }

  static async getEligibleOrders(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('perks', 'eligible'));
  }

  static async executePerk(orderId: string, perkData: any): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('perks', 'list')}/${orderId}/execute`, {
      method: 'POST',
      body: JSON.stringify(perkData),
    });
  }

  static async getFlagshipServices(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('perks', 'flagshipServices'));
  }

  // ====================
  // COMMISSION APIs
  // ====================

  static async getCommissionProjections(
    request: CommissionProjectionRequest,
  ): Promise<CommissionProjectionResponse> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('commission', 'projections'), {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  static async getCommissionDashboard(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('commission', 'dashboard'));
  }

  static async getCommissionTemplates(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('commission', 'templates'));
  }

  static async createCommissionTemplate(template: any): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('commission', 'templates'), {
      method: 'POST',
      body: JSON.stringify(template),
    });
  }

  static async getCommissionOverrides(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('commission', 'overrides'));
  }

  static async setCommissionOverride(templateId: string, config: any): Promise<any> {
    return apiFetchWithCircuitBreaker(
      `${getApiEndpoint('commission', 'overrides')}/${templateId}`,
      {
        method: 'PUT',
        body: JSON.stringify({ commission_config: config }),
      },
    );
  }

  // ====================
  // ORDERS APIs
  // ====================

  static async getOrders(params?: {
    status?: string;
    limit?: number;
    offset?: number;
    can_rate?: boolean;
  }): Promise<OrderResponse> {
    const queryParams = params ? '?' + new URLSearchParams(params as any) : '';
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('orders', 'list')}${queryParams}`);
  }

  static async getPartnerOrders(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('orders', 'partner'));
  }

  static async getOrdersInbox(params?: { scope?: string; limit?: number }): Promise<any> {
    const queryParams = params ? '?' + new URLSearchParams(params as any) : '';
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('orders', 'inbox')}${queryParams}`);
  }

  static async getInstalls(params?: {
    scope?: string;
    status?: string;
    limit?: number;
  }): Promise<any> {
    const queryParams = params ? '?' + new URLSearchParams(params as any) : '';
    return apiFetchWithCircuitBreaker(`/api/installs${queryParams}`);
  }

  static async getInstallScore(): Promise<any> {
    return apiFetchWithCircuitBreaker(`/api/partners/install-score`);
  }

  static async getPartnerStaff(tenantId: string): Promise<any> {
    return apiFetchWithCircuitBreaker(`/api/partners/${tenantId}/staff`);
  }

  static async getTechnicians(tenantId: string): Promise<any> {
    return apiFetchWithCircuitBreaker(`/api/partners/${tenantId}/technicians`);
  }

  static async getTickets(params?: { limit?: number }): Promise<any> {
    const queryParams = params ? '?' + new URLSearchParams(params as any) : '';
    return apiFetchWithCircuitBreaker(`/api/tickets${queryParams}`);
  }

  static async updatePartnerOrderStatus(
    orderId: string,
    status: string,
    notes?: string,
  ): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('orders', 'partner')}/${orderId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, notes }),
    });
  }

  static async confirmOrderReceipt(orderId: string): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('orders', 'list')}/${orderId}/receipt`, {
      method: 'POST',
    });
  }

  static async submitOrderRating(
    orderId: string,
    ratingData: { rating: number; review?: string },
  ): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('orders', 'list')}/${orderId}/rating`, {
      method: 'POST',
      body: JSON.stringify(ratingData),
    });
  }

  static async cancelOrder(orderId: string, reason?: string): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('orders', 'list')}/${orderId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ cancellation_reason: reason }),
    });
  }

  static async updateOrderStatus(orderId: string, status: string, notes?: string): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('orders', 'list')}/${orderId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, notes }),
    });
  }

  // ====================
  // SERVICES APIs
  // ====================

  static async getServices(): Promise<ServiceResponse> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('services', 'list'));
  }

  static async getPartnerServices(): Promise<ServiceResponse> {
    return apiFetchWithCircuitBreaker('/api/partners/services');
  }

  static async getReferralAnalytics(): Promise<any> {
    return apiFetchWithCircuitBreaker('/api/partners/analytics');
  }

  static async getReferralCode(): Promise<any> {
    return apiFetchWithCircuitBreaker('/api/partners/code');
  }

  static async getReferralTierStatus(): Promise<any> {
    return apiFetchWithCircuitBreaker('/api/partners/tier-status');
  }

  static async createService(serviceData: any): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('services', 'create'), {
      method: 'POST',
      body: JSON.stringify(serviceData),
    });
  }

  // ====================
  // BRANDS APIs
  // ====================

  static async getBrands(): Promise<BrandResponse> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('brands', 'list'));
  }

  static async createBrand(brandData: any): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('brands', 'create'), {
      method: 'POST',
      body: JSON.stringify(brandData),
    });
  }

  // ====================
  // SUPER ADMIN APIs
  // ====================

  static async getSuperAdminPartners(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('superAdmin', 'partners'));
  }

  static async getSuperAdminBalances(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('superAdmin', 'balances'));
  }

  static async adjustPartnerFunds(
    tenantId: string,
    adjustment: {
      amount_cents: number;
      reason: string;
      notes?: string;
    },
  ): Promise<any> {
    return apiFetchWithCircuitBreaker(
      getEndpoint(getApiEndpoint('superAdmin', 'fundAdjustments'), { tenantId }),
      {
        method: 'POST',
        body: JSON.stringify(adjustment),
      },
    );
  }

  static async getFinancialOverview(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('superAdmin', 'financialOverview'));
  }

  // ====================
  // ADMIN APIs (Phase 2: Comprehensive Super Admin)
  // ====================

  // Service Management
  static async getAdminServices(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('admin', 'services'));
  }

  static async createEnterpriseService(serviceData: any): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('admin', 'services'), {
      method: 'POST',
      body: JSON.stringify(serviceData),
    });
  }

  static async updateEnterpriseService(serviceId: string, serviceData: any): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('admin', 'services')}/${serviceId}`, {
      method: 'PUT',
      body: JSON.stringify(serviceData),
    });
  }

  static async toggleServiceFlagship(serviceId: string, flagship: boolean): Promise<any> {
    return apiFetchWithCircuitBreaker(
      `${getApiEndpoint('admin', 'services')}/${serviceId}/flagship`,
      {
        method: 'PUT',
        body: JSON.stringify({ flagship }),
      },
    );
  }

  static async getServiceAnalytics(): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('admin', 'services')}/analytics`);
  }

  // Catalog Management
  static async createCatalogInstance(catalogData: any): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('admin', 'catalogs'), {
      method: 'POST',
      body: JSON.stringify(catalogData),
    });
  }

  static async updateCatalogInstance(catalogId: string, catalogData: any): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('admin', 'catalogs')}/${catalogId}`, {
      method: 'PUT',
      body: JSON.stringify(catalogData),
    });
  }

  static async getCatalogAnalytics(): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('admin', 'catalogs')}/analytics`);
  }

  static async updateCatalogBranding(catalogId: string, branding: any): Promise<any> {
    return apiFetchWithCircuitBreaker(
      `${getApiEndpoint('admin', 'catalogs')}/${catalogId}/branding`,
      {
        method: 'POST',
        body: JSON.stringify({ branding }),
      },
    );
  }

  // Brand Management
  static async createOrUpdateBrand(brandData: any): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('admin', 'brands'), {
      method: 'POST',
      body: JSON.stringify(brandData),
    });
  }

  static async getBrandAnalytics(): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('admin', 'brands')}/analytics`);
  }

  static async getPartnerBrands(): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('admin', 'partners')}/brands`);
  }

  // Platform Analytics
  static async getPlatformOverview(): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('admin', 'analytics')}/overview`);
  }

  static async getAIOverview(): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('admin', 'analytics')}/ai-overview`);
  }

  static async getCatalogUsageAnalytics(): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('admin', 'analytics')}/catalogs`);
  }

  static async getRevenueAnalytics(): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('admin', 'analytics')}/revenue`);
  }

  static async getAdminOrders(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('admin', 'orders'));
  }

  static async getAdminOrderMetrics(): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('admin', 'orders')}/metrics`);
  }

  static async getIssuesRequiringAttention(): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('admin', 'orders')}/issues`);
  }

  static async getSupplierPerformance(): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('admin', 'orders')}/suppliers`);
  }

  static async getOrderStatusBreakdown(): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('admin', 'orders')}/status-breakdown`);
  }

  static async postAdminIntervention(data: any): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('admin', 'interventions')}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  static async getSupplierFulfillments(): Promise<any> {
    return apiFetchWithCircuitBreaker('/api/partners/orders/fulfillments');
  }

  static async getSupplierMetrics(): Promise<any> {
    return apiFetchWithCircuitBreaker('/api/partners/orders/metrics');
  }

  static async getSuperAdminSettings(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('superAdmin', 'settings'));
  }

  static async updateSuperAdminSettings(settings: any): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('superAdmin', 'settings'), {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  // ====================
  // TICKETING APIs
  // ====================

  static async getTicketAttachments(ticketId: string): Promise<any> {
    return apiFetchWithCircuitBreaker(`/api/ticketing/${ticketId}/attachments`);
  }

  static async uploadTicketAttachment(ticketId: string, attachmentData: any): Promise<any> {
    return apiFetchWithCircuitBreaker(`/api/ticketing/${ticketId}/attachments`, {
      method: 'POST',
      body: JSON.stringify(attachmentData),
    });
  }

  // ====================
  // CHAT APIs
  // ====================

  static async getChatMessages(roomId: string): Promise<any> {
    return apiFetchWithCircuitBreaker(`/api/chat/messages/${roomId}`);
  }

  static async sendChatMessage(roomId: string, messageData: any): Promise<any> {
    return apiFetchWithCircuitBreaker(`/api/chat/messages/${roomId}`, {
      method: 'POST',
      body: JSON.stringify(messageData),
    });
  }

  // ====================
  // CATALOG APIs
  // ====================

  static async getCatalogInstances(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('catalog', 'instances'));
  }

  static async getCatalogInstanceServices(instanceId: string, params?: any): Promise<any> {
    const queryParams = params ? '?' + new URLSearchParams(params as any) : '';
    return apiFetchWithCircuitBreaker(
      `${getApiEndpoint('catalog', 'instances')}/${instanceId}/services${queryParams}`,
    );
  }

  // ====================
  // PARTNER DASHBOARD APIs
  // ====================

  static async getPartnerDashboardOverview(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('partnerDashboard', 'overview'));
  }

  static async getPartnerDashboardServices(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('partnerDashboard', 'services'));
  }

  static async getPartnerDashboardAlerts(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('partnerDashboard', 'alerts'));
  }

  static async getPartnerDashboardNotifications(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('partnerDashboard', 'notifications'));
  }

  static async markAllNotificationsRead(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('partnerDashboard', 'markRead'), {
      method: 'POST',
    });
  }

  static async markNotificationRead(notificationId: string): Promise<any> {
    return apiFetchWithCircuitBreaker(
      `${getApiEndpoint('partnerDashboard', 'notifications')}/${notificationId}/read`,
      { method: 'POST' },
    );
  }

  // ====================
  // AUTHENTICATION APIs
  // ====================

  static async login(credentials: { email: string; password: string }): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('auth', 'login'), {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  static async logout(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('auth', 'logout'), { method: 'POST' });
  }

  static async getCurrentUser(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('auth', 'me'));
  }

  static async register(userData: any): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('auth', 'register'), {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  // ====================
  // RATINGS APIs
  // ====================

  static async submitRating(ratingData: any): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('ratings', 'customer'), {
      method: 'POST',
      body: JSON.stringify(ratingData),
    });
  }

  static async getRateableOrders(): Promise<OrderResponse> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('ratings', 'orders'));
  }

  // ====================
  // CART APIs
  // ====================

  static async addToCart(itemData: any): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('cart', 'items'), {
      method: 'POST',
      body: JSON.stringify(itemData),
    });
  }

  static async authenticateCart(cartData: any): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('cart', 'auth'), {
      method: 'POST',
      body: JSON.stringify(cartData),
    });
  }

  static async getCart(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('cart', 'customer'));
  }

  static async removeCartItem(itemId: string): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('cart', 'customer')}/items/${itemId}`, {
      method: 'DELETE',
    });
  }

  static async updateCartItemQuantity(itemId: string, quantity: number): Promise<any> {
    return apiFetchWithCircuitBreaker(`${getApiEndpoint('cart', 'customer')}/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify({ quantity }),
    });
  }

  static async saveCartItemForLater(itemId: string): Promise<any> {
    return apiFetchWithCircuitBreaker(
      `${getApiEndpoint('cart', 'customer')}/items/${itemId}/save-for-later`,
      { method: 'POST' },
    );
  }

  static async moveCartItemToCart(itemId: string): Promise<any> {
    return apiFetchWithCircuitBreaker(
      `${getApiEndpoint('cart', 'customer')}/items/${itemId}/move-to-cart`,
      { method: 'POST' },
    );
  }

  static async toggleCartItemSelection(itemId: string, selected: boolean): Promise<any> {
    return apiFetchWithCircuitBreaker(
      `${getApiEndpoint('cart', 'customer')}/items/${itemId}/toggle-selection`,
      {
        method: 'POST',
        body: JSON.stringify({ selected }),
      },
    );
  }

  static async checkoutCart(checkoutData: any): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('cart', 'checkout'), {
      method: 'POST',
      body: JSON.stringify(checkoutData),
    });
  }

  // ====================
  // HEALTH APIs
  // ====================

  static async healthCheck(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('health', 'check'));
  }

  static async getBuildInfo(): Promise<any> {
    return apiFetchWithCircuitBreaker(getApiEndpoint('health', 'buildInfo'));
  }

  // ====================
  // GENERIC API METHODS
  // ====================

  static async get(endpoint: string, options?: { params?: Record<string, any> }): Promise<any> {
    const url = options?.params
      ? `${endpoint}?${new URLSearchParams(options.params as any)}`
      : endpoint;
    return apiFetchWithCircuitBreaker(url);
  }

  static async post(endpoint: string, data?: any): Promise<any> {
    return apiFetchWithCircuitBreaker(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  static async put(endpoint: string, data?: any): Promise<any> {
    return apiFetchWithCircuitBreaker(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  static async patch(endpoint: string, data?: any): Promise<any> {
    return apiFetchWithCircuitBreaker(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  static async delete(endpoint: string): Promise<any> {
    return apiFetchWithCircuitBreaker(endpoint, { method: 'DELETE' });
  }
}

// Export ApiClient as 'api' for backward compatibility
export const api = ApiClient;

// Types are exported above with their declarations
